import React, { useState } from 'react';
import CustomerShell from '../../components/customer/CustomerShell';
import RedeemQRDialog from '../../components/customer/RedeemQRDialog';
import { useWalletBalance, useWalletTransactions } from '../../hooks/useWalletQueries';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Wallet, Clock, Sparkles, ArrowUpRight, ArrowDownLeft, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Hourglass, Coins, Info, QrCode
} from 'lucide-react';
import { formatDistanceToNowStrict, format, isPast } from 'date-fns';

const POINT_PRICE_INR = 1;  // 1 Point = ₹1 (Phase 1 spec)

// ── Helpers ───────────────────────────────────────────────────────────────────
const TYPE_META = {
  COVER_CREDIT:   { label: 'Cover Charge',    icon: ArrowUpRight,  color: '#4ADE80' },
  LOYALTY_CREDIT: { label: 'Loyalty Bonus',   icon: Sparkles,      color: '#D4A843' },
  PROMO_CREDIT:   { label: 'Promo Bonus',     icon: Sparkles,      color: '#60A5FA' },
  REDEEM:         { label: 'Redeemed',        icon: ArrowDownLeft, color: '#FF4D4D' },
  REFUND:         { label: 'Refund',          icon: RefreshCw,     color: '#60A5FA' },
  REVERSAL:       { label: 'Reversal',        icon: RefreshCw,     color: '#FBB924' },
};

const STATUS_META = {
  ACTIVE:   { label: 'Active',   color: '#4ADE80', icon: CheckCircle2 },
  EXPIRED:  { label: 'Expired',  color: '#6B6B6B', icon: XCircle      },
  REDEEMED: { label: 'Redeemed', color: '#B9B9B9', icon: CheckCircle2 },
};

function StatCard({ icon: Icon, label, value, sub, tone = 'gold', testId }) {
  const palette = {
    gold:   { bg: 'rgba(212,168,67,0.08)',  border: 'rgba(212,168,67,0.25)',  icon: '#D4A843' },
    green:  { bg: 'rgba(74,222,128,0.08)',  border: 'rgba(74,222,128,0.25)',  icon: '#4ADE80' },
    red:    { bg: 'rgba(255,77,77,0.08)',   border: 'rgba(255,77,77,0.25)',   icon: '#FF4D4D' },
    blue:   { bg: 'rgba(96,165,250,0.08)',  border: 'rgba(96,165,250,0.25)',  icon: '#60A5FA' },
    muted:  { bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.07)', icon: '#B9B9B9' },
  }[tone];

  return (
    <Card
      data-testid={testId}
      className="p-5 rounded-2xl border"
      style={{ background: palette.bg, borderColor: palette.border }}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#B9B9B9] uppercase tracking-widest">{label}</p>
        <Icon size={16} style={{ color: palette.icon }} />
      </div>
      <p className="text-3xl font-bold text-[#F2F2F2] font-mono">{value}</p>
      {sub && <p className="text-xs text-[#6B6B6B] mt-1">{sub}</p>}
    </Card>
  );
}

export default function WalletPage() {
  const { data: bal, isLoading: balLoading, refetch: refetchBalance } = useWalletBalance();
  const { data: txnData, isLoading: txnLoading } = useWalletTransactions({ page: 1, limit: 20 });
  const [showQR, setShowQR] = useState(false);

  const available     = bal?.available ?? 0;
  const expiring      = bal?.expiring ?? 0;
  const nonExpiring   = bal?.non_expiring ?? 0;
  const expiringSoon  = bal?.expiring_soon ?? 0;
  const nextExpiry    = bal?.next_expiry_at ? new Date(bal.next_expiry_at) : null;
  const txns          = txnData?.items || [];

  const showExpiryBanner =
    !!nextExpiry && !isPast(nextExpiry) && expiringSoon > 0;

  return (
    <CustomerShell>
      <div data-testid="wallet-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] flex items-center gap-3">
              <Wallet size={26} className="text-[#D4A843]" /> Scratch Points
            </h1>
            <p className="text-sm text-[#B9B9B9] mt-1">
              1 Point = ₹{POINT_PRICE_INR} · Cover-charge points expire 12 hours after credit.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="wallet-show-qr-btn"
              size="sm"
              className="gap-2 btn-gold rounded-xl"
              onClick={() => setShowQR(true)}
              disabled={available <= 0}
              title={available <= 0 ? 'No points to redeem' : 'Show QR at the counter to redeem points'}
            >
              <QrCode size={14} /> Show QR to Redeem
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="wallet-refresh-btn"
              className="gap-2 bg-transparent border-[rgba(255,255,255,0.1)] text-[#B9B9B9]"
              onClick={() => refetchBalance()}
              disabled={balLoading}
            >
              <RefreshCw size={14} className={balLoading ? 'animate-spin' : ''} /> Refresh
            </Button>
          </div>
        </div>

        {/* Expiring soon banner */}
        {showExpiryBanner && (
          <Card
            data-testid="wallet-expiring-banner"
            className="p-5 rounded-2xl border"
            style={{
              background: 'linear-gradient(135deg, rgba(255,77,77,0.10) 0%, rgba(212,168,67,0.06) 100%)',
              borderColor: 'rgba(255,77,77,0.30)',
            }}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,77,77,0.18)' }}>
                <AlertTriangle size={18} className="text-[#FF4D4D]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[#F2F2F2] text-base font-semibold mb-1">
                  ⏳ {expiringSoon} points expiring soon
                </h3>
                <p className="text-[#B9B9B9] text-xs leading-relaxed">
                  Your cover-charge points will expire {formatDistanceToNowStrict(nextExpiry, { addSuffix: true })}
                  {' '}(at {format(nextExpiry, 'h:mm a')}). Visit the venue or show your QR at the counter to redeem.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testId="wallet-stat-available"    icon={Coins}     tone="gold"  label="Available"      value={available}    sub={`≈ ₹${available}`} />
          <StatCard testId="wallet-stat-expiring"     icon={Hourglass} tone="red"   label="Expiring (12h)" value={expiring}     sub={nextExpiry ? `Next: ${format(nextExpiry, 'MMM d, h:mm a')}` : '—'} />
          <StatCard testId="wallet-stat-non-expiring" icon={Sparkles}  tone="blue"  label="Loyalty (never expires)" value={nonExpiring} sub="Coming soon" />
          <StatCard testId="wallet-stat-redeemed"     icon={ArrowDownLeft} tone="muted" label="Lifetime Redeemed" value={bal?.total_redeemed ?? 0} sub={`${bal?.total_credited ?? 0} credited all-time`} />
        </div>

        {/* How it works */}
        <Card className="p-5 rounded-2xl border" style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(212,168,67,0.12)' }}>
              <Info size={15} className="text-[#D4A843]" />
            </div>
            <div className="text-sm text-[#B9B9B9] leading-relaxed">
              <p className="text-[#F2F2F2] font-semibold mb-1">How Scratch Points work</p>
              <ul className="space-y-1 list-disc list-inside text-xs">
                <li>Every paid check-in credits your wallet — <span className="text-[#D4A843]">1 point per ₹1 cover charge</span>.</li>
                <li>Cover-charge points expire <span className="text-[#FF4D4D]">12 hours after credit</span> — use them on the same visit at the bar or food counter.</li>
                <li>Tap <strong className="text-[#D4A843]">Show QR to Redeem</strong> at the bar / counter — staff scans it and points are deducted instantly.</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Transactions */}
        <div>
          <h2 className="text-lg font-semibold text-[#F2F2F2] mb-3 flex items-center gap-2">
            <Clock size={16} className="text-[#D4A843]" /> Recent Activity
          </h2>

          {txnLoading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw size={20} className="animate-spin text-[#D4A843]" />
            </div>
          ) : txns.length === 0 ? (
            <Card className="p-8 rounded-2xl text-center" style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.07)' }}>
              <Wallet size={32} className="mx-auto mb-3 text-[#6B6B6B]" />
              <p className="text-[#F2F2F2] font-semibold">No activity yet</p>
              <p className="text-xs text-[#6B6B6B] mt-1">Pay your first cover charge to start earning Scratch Points.</p>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="wallet-txn-list">
              {txns.map((t) => {
                const meta   = TYPE_META[t.type] || { label: t.type, icon: Coins, color: '#B9B9B9' };
                const status = STATUS_META[t.status] || STATUS_META.ACTIVE;
                const Icon   = meta.icon;
                const StatusIcon = status.icon;
                const isCredit = t.points > 0;
                return (
                  <Card
                    key={t.id}
                    data-testid={`wallet-txn-${t.id}`}
                    className="p-4 rounded-xl border flex items-center gap-4"
                    style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.07)' }}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: `${meta.color}1A` }}
                    >
                      <Icon size={18} style={{ color: meta.color }} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-[#F2F2F2] font-semibold">{meta.label}</p>
                        <span
                          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ background: `${status.color}1A`, color: status.color }}
                        >
                          <StatusIcon size={9} /> {status.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-[#6B6B6B] mt-0.5">
                        {t.created_at ? format(new Date(t.created_at), 'MMM d, yyyy · h:mm a') : '—'}
                        {t.expires_at && (
                          <>
                            {' · expires '}
                            {format(new Date(t.expires_at), 'MMM d, h:mm a')}
                          </>
                        )}
                        {t.source && <> · {t.source}</>}
                      </p>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p
                        className="text-base font-bold font-mono"
                        style={{ color: isCredit ? '#4ADE80' : '#FF4D4D' }}
                      >
                        {isCredit ? '+' : ''}{t.points}
                      </p>
                      <p className="text-[10px] text-[#6B6B6B]">pts</p>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <RedeemQRDialog open={showQR} onClose={() => setShowQR(false)} />
    </CustomerShell>
  );
}
